import {
  unexplainedConnectionError,
  type AutoSyncProvider,
  type ProviderConnectionState,
} from '../../shared/src/types/connections';

export interface ConnectionDevice {
  deviceId: string;
  deviceName: string;
  platform: string;
  capabilities: {
    read: boolean;
    write: boolean;
    readPlatforms: string[];
    sendPlatforms: string[];
    connections?: ProviderConnectionState[];
    webSyncPaused?: boolean;
  };
  status: 'online' | 'offline';
  lastSeenAt: number;
  lastSyncAt?: number;
  lastSyncStatus?: 'idle' | 'syncing' | 'success' | 'error';
  lastSyncMessageCount?: number;
  lastSyncNewMessages?: number;
  lastSyncError?: string;
}

export function providerDevices(provider: AutoSyncProvider, devices: ConnectionDevice[]) {
  return devices.filter(
    (device) =>
      device.capabilities.connections?.some((row) => row.provider === provider && row.enabled) ||
      (device.capabilities.read && device.capabilities.readPlatforms.includes(provider))
  );
}

export function providerDeviceStatus(
  provider: AutoSyncProvider,
  device: ConnectionDevice,
  live: boolean
): { label: string; tone: 'neutral' | 'success' | 'warning'; error?: string; lastSync?: number } {
  if (!live) return { label: 'Reconnecting…', tone: 'neutral' };
  if (device.status === 'offline') return { label: 'Mac offline', tone: 'neutral' };
  const paused = device.capabilities.webSyncPaused === true;
  const connection = device.capabilities.connections?.find((row) => row.provider === provider);
  if (
    connection &&
    (!connection.enabled ||
      ['error', 'permission_required', 'setup_required'].includes(connection.status))
  )
    return {
      label:
        connection.status === 'permission_required'
          ? 'Allow access on your Mac'
          : 'Needs attention on your Mac',
      tone: 'warning',
      error: connection.error,
    };
  // An older Mac reports one error for everything; it is shown once below the cards.
  if (!connection && device.lastSyncStatus === 'error')
    return { label: 'Mac needs attention', tone: 'warning' };
  // The Mac still reads the app, but nothing new reaches the web until it resumes.
  if (paused) return { label: 'Paused on Mac', tone: 'neutral' };
  // A Mac that reports each app's health speaks for that app alone; older Macs only have totals.
  if (connection ? connection.status === 'syncing' : device.lastSyncStatus === 'syncing')
    return { label: 'Syncing…', tone: 'neutral' };
  return {
    label: 'Connected on Mac',
    tone: 'success',
    lastSync: connection?.lastSync ? Date.parse(connection.lastSync) : device.lastSyncAt,
  };
}

/**
 * The one line a Mac earns above the cards: that it paused web sync, or a failure no app card
 * already shows, such as an upload error.
 */
export function deviceSyncNotice(
  device: ConnectionDevice
): { text: string; failed: boolean } | undefined {
  if (device.status !== 'online') return undefined;
  if (device.capabilities.webSyncPaused)
    return {
      text: `Web sync is paused on ${device.deviceName}. New messages stay on that Mac until you resume it there.`,
      failed: false,
    };
  if (device.lastSyncStatus !== 'error') return undefined;
  const error = device.lastSyncError
    ? unexplainedConnectionError(
        device.lastSyncError,
        (device.capabilities.connections ?? []).flatMap((row) =>
          row.enabled && row.error ? [row.error] : []
        )
      )
    : 'Sync needs attention. Open SoulThread on this Mac.';
  return error ? { text: `${device.deviceName}: ${error}`, failed: true } : undefined;
}
